<template>
  <div class="flex flex-wrap items-center gap-1.5">
    <!-- Search, with suggestions from all of the user's jobs (not just the page that is loaded) -->
    <div class="relative flex-1 min-w-[10rem] sm:max-w-xs">
      <SearchSuggest v-model="search" :items="suggestions" :loading="suggesting" compact wide placeholder="Search jobs, companies, locations" empty-title="Latest jobs" />
    </div>

    <!-- Which jobs: the active ones (not ignored), the bookmarked ones, or the ignored ones -->
    <div role="group" aria-label="Which jobs to show" class="inline-flex p-0.5 rounded-lg bg-gray-100 dark:bg-gray-800">
      <button v-for="option in viewOptions" :key="option.value" type="button" @click="$emit('change', { view: option.value })"
        :aria-pressed="filters.view === option.value"
        class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[13px] font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
        :class="filters.view === option.value
          ? 'bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white shadow-sm'
          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'">
        {{ option.label }}
        <span class="text-[11px] font-bold tabular-nums" :class="filters.view === option.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'">{{ option.count }}</span>
      </button>
    </div>

    <FilterDropdown label="Job title" :options="titleOptions" :model-value="filters.job_title_id" @update:model-value="$emit('change', { job_title_id: $event })" />
    <FilterDropdown label="Location" :options="locationOptions" :model-value="filters.job_location_id" @update:model-value="$emit('change', { job_location_id: $event })" />
    <FilterDropdown label="Status" :options="statusOptions" :model-value="filters.status" @update:model-value="$emit('change', { status: $event })" />
    <FilterDropdown label="Posted" :options="POSTED_OPTIONS" :model-value="filters.posted" neutral="" @update:model-value="$emit('change', { posted: $event })" />

    <span class="hidden md:block w-px h-4 bg-gray-200 dark:bg-gray-700 mx-0.5" aria-hidden="true"></span>

    <FilterDropdown label="Sort" :options="SORT_OPTIONS" :model-value="filters.sort" neutral="newest" @update:model-value="$emit('change', { sort: $event })" />

    <button v-if="active" type="button" @click="$emit('clear')"
      class="inline-flex items-center gap-1 h-8 px-2 text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      Clear
    </button>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import FilterDropdown from './FilterDropdown.vue'
import SearchSuggest from '../shared/SearchSuggest.vue'
import { useFetchedJobsStore } from 'stores/fetchedJobs.js'

const props = defineProps({
  filters: { type: Object, required: true },
  titles: { type: Array, default: () => [] },
  locations: { type: Array, default: () => [] },
  counts: { type: Object, required: true },
  active: { type: Boolean, default: false },
})
const emit = defineEmits(['change', 'clear'])

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest posted' },
  { value: 'recently_saved', label: 'Recently saved' },
]

// How recently a job was posted. The values are what the server accepts (FetchedJob::POSTED_WITHIN).
const POSTED_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '1h', label: 'Past hour' },
  { value: '24h', label: 'Past 24 hours' },
  { value: '7d', label: 'Past week' },
  { value: '30d', label: 'Past month' },
]

const viewOptions = computed(() => [
  { value: 'active', label: 'Active', count: props.counts.active },
  { value: 'bookmarked', label: 'Bookmarked', count: props.counts.bookmarked },
  { value: 'ignored', label: 'Ignored', count: props.counts.ignored },
])

const titleOptions = computed(() => [{ value: '', label: 'All job titles' }, ...props.titles.map((title) => ({ value: title.id, label: title.name }))])
const locationOptions = computed(() => [{ value: '', label: 'All locations' }, ...props.locations.map((location) => ({ value: location.id, label: location.name }))])
const statusOptions = computed(() => [
  { value: 'all', label: 'All jobs' },
  { value: 'not_applied', label: `Not applied (${props.counts.all - props.counts.applied})` },
  { value: 'applied', label: `Applied (${props.counts.applied})` },
])

// Typing in the search box waits a moment, so the list is not reloaded on every key.
const search = ref(props.filters.q)
let timer = null

watch(search, (value) => {
  clearTimeout(timer)
  timer = setTimeout(() => emit('change', { q: value }), 300)
  askForSuggestions(value)
})

// The suggestions come from the server (the list on screen is only one page of the jobs), a moment after typing stops. Only the newest answer is used.
const store = useFetchedJobsStore()
const suggestions = ref([])
const suggesting = ref(false)
let suggestTimer = null
let suggestRequest = 0

function askForSuggestions(value) {
  clearTimeout(suggestTimer)
  suggesting.value = true
  suggestTimer = setTimeout(async () => {
    const request = ++suggestRequest
    const found = await store.suggest(value)
    if (request !== suggestRequest) return
    suggestions.value = found
    suggesting.value = false
  }, 200)
}

// Before anything is typed the box offers the latest jobs; a change of view (active, bookmarked, ignored) makes them stale.
askForSuggestions('')
watch(() => props.filters.view, () => askForSuggestions(search.value))

// Clearing the filters elsewhere empties the box too.
watch(() => props.filters.q, (value) => { if (value !== search.value) search.value = value })

onBeforeUnmount(() => { clearTimeout(timer); clearTimeout(suggestTimer); suggestRequest += 1 })
</script>
